import React, { useState, useEffect } from 'react';
import { GRCControl, CISCheckScript } from '../../types/grc';
import { grcService } from '../../services/grcService';
import { 
  Terminal, Code, Play, Copy, Check, RefreshCw, 
  AlertTriangle, CheckCircle, XCircle, Settings,
  FileCode, Download, Sparkles
} from 'lucide-react';

interface CISScriptViewerProps {
  control: GRCControl;
  onClose: () => void;
}

type ScriptType = 'powershell' | 'bash' | 'python';

export const CISScriptViewer: React.FC<CISScriptViewerProps> = ({
  control,
  onClose
}) => {
  const [scriptType, setScriptType] = useState<ScriptType>('powershell');
  const [script, setScript] = useState<CISCheckScript | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showRemediation, setShowRemediation] = useState(false);

  useEffect(() => {
    loadScript();
  }, [control.id, scriptType]);

  const loadScript = async () => {
    setLoading(true);
    const data = await grcService.getCISCheckScript(control.id, scriptType);
    setScript(data);
    setLoading(false);
  };

  const handleGenerateScript = async () => {
    setGenerating(true);
    const result = await grcService.generateCISScript(control.id, scriptType);
    if (result.success && result.script) {
      setScript(result.script);
    } else {
      alert(result.error || 'Failed to generate script');
    }
    setGenerating(false);
  };

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!script) return;
    
    const ext = scriptType === 'powershell' ? 'ps1' : scriptType === 'bash' ? 'sh' : 'py';
    const blob = new Blob([script.script_content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cis_check_${control.control_code.replace(/\./g, '_')}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getScriptIcon = (type: ScriptType) => {
    switch (type) {
      case 'powershell': return '🔷';
      case 'bash': return '🐧';
      case 'python': return '🐍';
    }
  };

  const isAutomatable = control.check_type === 'automated' || control.check_type === 'hybrid';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Terminal className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{control.control_code}</h3>
                <p className="text-sm text-gray-400 truncate max-w-md">{control.title}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <XCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Check Type Indicator */}
        <div className="px-4 py-3 bg-gray-800 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
              control.check_type === 'automated' 
                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                : control.check_type === 'hybrid'
                ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
            }`}>
              {control.check_type === 'automated' && <CheckCircle className="w-4 h-4" />}
              {control.check_type === 'hybrid' && <Settings className="w-4 h-4" />}
              {control.check_type === 'manual' && <AlertTriangle className="w-4 h-4" />}
              {control.check_type} check
            </span>
            
            {!isAutomatable && (
              <span className="text-sm text-gray-400">
                This control requires manual verification
              </span>
            )}
          </div>

          {/* Script Type Selector */}
          <div className="flex items-center gap-1 bg-gray-700 rounded-lg p-1">
            {(['powershell', 'bash', 'python'] as ScriptType[]).map(type => (
              <button
                key={type}
                onClick={() => setScriptType(type)}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                  scriptType === type
                    ? 'bg-gray-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {getScriptIcon(type)} {type}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-gray-600 border-t-green-400 rounded-full animate-spin" />
            </div>
          ) : script ? (
            <div className="p-4 space-y-4">
              {/* Verification Script */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <Code className="w-4 h-4" />
                    Verification Script
                  </h4>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleCopy(script.script_content)}
                      className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
                    >
                      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                    <button
                      onClick={handleDownload}
                      className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
                    >
                      <Download className="w-3 h-3" />
                      Download
                    </button>
                  </div>
                </div>
                <pre className="bg-black rounded-lg p-4 overflow-x-auto text-sm font-mono text-green-400 border border-gray-700">
                  {script.script_content}
                </pre>
              </div>

              {/* Expected Output */}
              {script.expected_output && (
                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Expected Output (Compliant)
                  </h4>
                  <pre className="bg-gray-800 rounded-lg p-4 text-sm font-mono text-gray-300 border border-gray-700">
                    {script.expected_output}
                  </pre>
                </div>
              )}

              {/* Remediation Script */}
              {script.remediation_script && (
                <div>
                  <button
                    onClick={() => setShowRemediation(!showRemediation)}
                    className="text-sm font-medium text-yellow-400 mb-2 flex items-center gap-2 hover:text-yellow-300"
                  >
                    <AlertTriangle className="w-4 h-4" />
                    {showRemediation ? 'Hide' : 'Show'} Remediation Script
                  </button>
                  {showRemediation && (
                    <pre className="bg-yellow-900/20 rounded-lg p-4 overflow-x-auto text-sm font-mono text-yellow-400 border border-yellow-700/50">
                      {script.remediation_script}
                    </pre>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              {isAutomatable ? (
                <>
                  <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mb-4">
                    <FileCode className="w-8 h-8 text-gray-500" />
                  </div>
                  <h4 className="text-lg font-medium text-white mb-2">No Script Available</h4>
                  <p className="text-sm text-gray-400 text-center mb-6 max-w-md">
                    Generate an automated verification script for this CIS control using AI.
                  </p>
                  <button
                    onClick={handleGenerateScript}
                    disabled={generating}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 flex items-center gap-2 disabled:opacity-50"
                  >
                    {generating ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Generate {scriptType} Script with AI
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mb-4">
                    <Settings className="w-8 h-8 text-blue-400" />
                  </div>
                  <h4 className="text-lg font-medium text-white mb-2">Manual Verification Required</h4>
                  <p className="text-sm text-gray-400 text-center max-w-md mb-6">
                    This control requires manual review and cannot be fully automated. 
                    Follow the verification steps below:
                  </p>
                  <div className="bg-gray-800 rounded-lg p-4 w-full max-w-lg border border-gray-700">
                    <h5 className="text-sm font-medium text-white mb-3">Manual Verification Steps:</h5>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-gray-300">
                      <li>Review the control requirements in the policy documentation</li>
                      <li>Interview relevant stakeholders or process owners</li>
                      <li>Request and review supporting evidence</li>
                      <li>Document findings and compliance status</li>
                      <li>Upload evidence using the Evidence button</li>
                    </ol>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-800 border-t border-gray-700 flex justify-between items-center">
          <div className="text-xs text-gray-500">
            {script && `Last generated: ${new Date().toLocaleDateString()}`}
          </div>
          <div className="flex items-center gap-3">
            {script && isAutomatable && (
              <button
                onClick={handleGenerateScript}
                disabled={generating}
                className="px-4 py-2 text-gray-400 hover:text-white flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
                Regenerate
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
